import { ReactNode, useCallback, useEffect } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { logError } from 'services/logging/logging';
import { useHasAccessToOrderForm } from 'services/repositories/orderForms/orderForms';
import { BackendError, GenericError } from 'support/helpers/errors/errors';
import { OrderFormsRoutes } from 'support/types';
import { LoadingPage } from 'components/Loading/LoadingPage';
import { NotFoundPage } from 'components/Page/NotFoundPage';

export function AuthorizationGuard({ children }: { children: ReactNode }) {
  const params = useParams();
  const navigate = useNavigate();

  const redirectToUnauthorized = useCallback(() => {
    const unauthorizedPath = generatePath(OrderFormsRoutes.unauthorized, {
      supplierSlug: params.supplierSlug || '',
    });

    navigate(unauthorizedPath, {
      replace: true,
    });
  }, [navigate, params.supplierSlug]);

  const {
    isError,
    error,
    isLoading: isCheckingForAuthorization,
    isSuccess,
  } = useHasAccessToOrderForm({
    variables: { supplierSlugName: params.supplierSlug },
    retry: false,
  });

  useEffect(() => {
    if (!isError) {
      return;
    }

    const errorStatus = (error as BackendError).status;

    if (errorStatus === 404) {
      // let it render "not found" page
      return;
    }

    if (errorStatus !== 403) {
      logError(new GenericError('Order Forms: unknown error getting order form', error));
    }

    redirectToUnauthorized();
  }, [isError, error, redirectToUnauthorized]);

  if (isError && (error as BackendError).status === 404) {
    return <NotFoundPage />;
  }

  const shouldRenderChildren = !isCheckingForAuthorization && !isError && isSuccess;

  return shouldRenderChildren ? (
    <>{children}</>
  ) : (
    <div className="mx-auto h-screen">
      <LoadingPage />
    </div>
  );
}
